// Types
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { MemorabiliaProductType, UserRole } from 'api/graphql-global-types';
import { GetMemorabiliaMinimalInfo_getMemorabilia_entities } from 'api/memorabilia/types/GetMemorabiliaMinimalInfo';
// Constants
import { PRODUCTS } from 'constants/routes';
// Hooks
import { useBreakpoints } from 'hooks';
// Ui
import Text from 'ui3/Text/Text';
import LinkButton from 'ui3/LinkButton/LinkButton';
import ProductCard from 'ui3/Card/ProductCard';
// Helpers
import { formatPrice } from 'helpers/formatPrice';
// Styles
import styles from './Memorabilia.module.scss';

type MemorabiliaProps = {
  store: GetStoreBySlug_store;
  memorabilia?: GetMemorabiliaMinimalInfo_getMemorabilia_entities | null;
  isPage?: boolean;
};

const Memorabilia = ({ store, memorabilia }: MemorabiliaProps) => {
  const { isBreakpointL } = useBreakpoints();
  const memorabiliaTitle = memorabilia?.title || '';
  const description = memorabilia?.description || '';
  const imageFileUrl =
    memorabilia?.images?.find(({ isMainImage }) => isMainImage)?.url || '';
  const price = memorabilia?.price || 0;
  const memorabiliaUrl = memorabilia?.slug || '';
  const storeSlug = memorabilia?.store.slug || '';

  const isAthlete = store.role === UserRole.Athlete;
  const isOrganization = store.role === UserRole.Organization;
  const isContentCreator = store.role === UserRole.ContentCreator;

  const pageUrl = isAthlete
    ? `${PRODUCTS}?athleteIds=${store.id}`
    : isOrganization
    ? `${PRODUCTS}?organizationIds=${store.id}`
    : isContentCreator
    ? `${PRODUCTS}?contentCreatorIds=${store.id}`
    : '';

  const memorabiliaProductType = memorabilia?.memorabiliaProductType || '';
  const isExternalLink = pageUrl?.startsWith('http');
  const seeAllText = `See All ${
    memorabiliaProductType === MemorabiliaProductType.Memorabilia
      ? 'Memorabilia'
      : 'Products'
  }`;
  const showText = `Show ${
    memorabiliaProductType === MemorabiliaProductType.Memorabilia
      ? 'Memorabilia'
      : 'Products'
  }`;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <Text as="h2" variant="h5">
          Epic Memorabilia
        </Text>
        <LinkButton
          link={pageUrl}
          variant="secondary"
          subVariant="text"
          icon="chevronRight"
          iconPosition="right"
          iconSize={16}
          className={styles.showLinkButton}
          target={isExternalLink ? '_blank' : undefined}
          rel={isExternalLink ? 'noopener' : undefined}
        >
          {showText}
        </LinkButton>
        <LinkButton
          link={pageUrl}
          variant="secondary"
          subVariant="text"
          icon="chevronRight"
          iconPosition="right"
          iconSize={16}
          className={styles.seeAllLinkButton}
          target={isExternalLink ? '_blank' : undefined}
          rel={isExternalLink ? 'noopener' : undefined}
        >
          {seeAllText}
        </LinkButton>
      </div>
      <div className={styles.mainBlock}>
        <div className={styles.productCardWrapper}>
          <ProductCard
            className={styles.productCard}
            type={
              memorabilia?.memorabiliaProductType ??
              MemorabiliaProductType.Memorabilia
            }
            imageUrl={imageFileUrl}
            title={memorabiliaTitle}
            size="large"
          />
        </div>

        <div className={styles.body}>
          <Text variant={isBreakpointL ? 'h3' : 'h6'} className={styles.title}>
            {memorabiliaTitle}
          </Text>
          <Text
            variant={isBreakpointL ? 'body1Regular16' : 'body2Regular'}
            color="lights-medium"
            className={styles.description}
          >
            {description}
          </Text>
          <div className={styles.bookContainer}>
            <div className={styles.buttonWithPrice}>
              <span className={styles.priceText}>{formatPrice(price)}</span>
              <LinkButton
                target="_blank"
                rel="noreferrer"
                link={
                  storeSlug ? `/${storeSlug}/memorabilia/${memorabiliaUrl}` : ''
                }
                className={styles.linkButton}
              >
                Buy Now
              </LinkButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Memorabilia;
